/* eslint-disable indent */
import './style.scss';

import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  downloadPdfFile as downloadPdfPrequalification,
  downloadDocxFile as downloadDocxPrequalification,
} from 'apis/prequalificationReviews.api';
import {
  downloadPdfFile as downloadPdfSpecializeReview,
  downloadDocxFile as downloadDocxSpecializeReview,
} from 'apis/specializeReviews.api';
import {
  downloadPdfFile as downloadPdfFinancialReview,
  downloadDocxFile as downloadDocxFinancialReview,
} from 'apis/financialReviews.api';
import {
  downloadPdfFile as downloadPdfMidtermReview,
  downloadDocxFile as downloadDocxMidtermReview,
} from 'apis/midtermReviews.api';
import {
  downloadPdfFile as downloadPdfAcceptanceReview,
  downloadDocxFile as downloadDocxAcceptanceReview,
} from 'apis/acceptanceReviews.api';
import { getAllVersions } from 'apis/projectProposals.api';
import { updateMemberAttendance } from 'apis/councils.api';
import { toast } from 'layout';
import { TOAST, COUNCIL_TYPE_CODE, TIMEZONE_VN } from 'constant';
import { AbilityContext } from 'components/Can';
import {
  isMemberAllowedReview,
  getAcadamicLevelShortHand,
  getOrganizationNameById,
  toNonAccentVietnamese,
  is1593,
} from 'utils/func';
import {
  downloadDocxFile as downloadDocxDynamicReview,
  downloadPdfFile as downloadPdfDynamicReview,
} from 'apis/reviews.api';
import SendMailRemindAllMembersDialog from '../../SendRemindMembersDialog';
import ViewMailLogs from '../../ViewMailLogs';

dayjs.extend(utc);
dayjs.extend(timezone);
export default function Members({
  refetch,
  members,
  council,
  councilType,
  organizations,
  /* eslint-disable no-unused-vars */
  setIsFormModalOpen,
  setIsDetailModalOpen,
  setCouncilMemberId,
  projectProposalId,
  councilId,
  isShowScore,
  isShowProposedExpenditure,
  isShowResult,
  reviewDeadline,
  isShowValidScore,
  isShowReview,
  isReadOnly,
  isHasActionColumn,
  hasProjects,
  yearProposed,
  template,
  isShowStatus,
  isShowResultReviewPrequalification,
}) {
  // #region Data
  const { t } = useTranslation();
  const { projectId, summaryReportId } = useParams();
  const ability = useContext(AbilityContext);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  const [isVisibleConfirmSendRemind, setIsVisibleConfirmSendRemind] = useState(false);
  const [isViewMailLogs, setIsViewMailLogs] = useState(false);

  // #endregion Data

  // Add mutation for updating member attendance
  const updateMemberAttendanceMutation = useMutation(updateMemberAttendance, {
    onSuccess: () => {
      toast(TOAST.SUCCESS, t('council.member.attendance.updateSuccess'));
      refetch?.();
    },
    onError: (error) => {
      toast(TOAST.ERROR, error?.message || t('council.member.attendance.updateError'));
    },
  });

  const handleAttendanceChange = (memberId, isPresent) => {
    updateMemberAttendanceMutation.mutate({
      councilMemberId: memberId,
      isPresent,
    });
  };

  // #endregion Data

  // #endregion Data
  const handleNavigateReviewDetail = (value) => {
    const queryParams = `councilMemberId=${value?.id}&councilId=${councilId}&userId=${value?.contact?.user?.id}`;
    const typeProjects = isReadOnly ? 'projects-submit' : 'projects-revise';
    let urlTo = '';
    if (councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      urlTo = `/${typeProjects}/${projectProposalId}/council/${councilId}/prequalification-review/detail?${queryParams}`;
    }
    if (councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      urlTo = `/${typeProjects}/${projectProposalId}/council/${councilId}/specialize-review/detail?${queryParams}`;
    }
    if (councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      urlTo = `/${typeProjects}/${projectProposalId}/council/${councilId}/financial-review/detail?${queryParams}`;
    }
    if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      urlTo = `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/midterm-document/midterm-review/detail?${queryParams}`;
    }
    if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      urlTo = `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/acceptance-document/acceptance-review/detail-m08?${queryParams}`;
    }
    if (councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      urlTo = `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/liquidation-document/liquidation-review/detail-m08?${queryParams}`;
    }
    return urlTo;
  };

  const handleNavigateCreateOrUpdateReview = (value) => {
    const queryParams = `councilMemberId=${value?.id}&councilId=${councilId}&userId=${value?.contact?.user?.id}`;

    if (councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      if (value?.projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/projects-revise/${projectProposalId}/council/${councilId}/prequalification-review/edit?${queryParams}`;
      }
      return `/projects-revise/${projectProposalId}/council/${councilId}/prequalification-review/create?${queryParams}`;
    }

    if (councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      if (value?.projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/projects-revise/${projectProposalId}/council/${councilId}/specialize-review/edit?${queryParams}`;
      }
      return `/projects-revise/${projectProposalId}/council/${councilId}/specialize-review/create?${queryParams}`;
    }

    if (councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      if (value?.projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/projects-revise/${projectProposalId}/council/${councilId}/financial-review/edit?${queryParams}`;
      }
      return `/projects-revise/${projectProposalId}/council/${councilId}/financial-review/create?${queryParams}`;
    }

    if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      if (value?.projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/midterm-document/midterm-review/edit?${queryParams}`;
      }
      return `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/midterm-document/midterm-review/create?${queryParams}`;
    }

    if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      if (value?.projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/acceptance-document/acceptance-review/edit?${queryParams}`;
      }
      return `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/acceptance-document/acceptance-review/create?${queryParams}`;
    }

    if (councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      if (value?.projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/liquidation-document/liquidation-review/edit?${queryParams}`;
      }
      return `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/liquidation-document/liquidation-review/create?${queryParams}`;
    }

    return '';
  };

  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId),
    {
      enabled: !!projectProposalId,
    }
  );

  const version1 = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 1) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);
  const prequalificationVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 2) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);

  const specializationVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 3) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);

  const latestVersion = useMemo(
    () => allProjectProposalVersionsData?.data?.reduce((a, b) => (a?.version > b?.version ? a : b)),
    [allProjectProposalVersionsData]
  );

  // #region Event
  const {
    mutate: mutateDownloadPdfPrequalificationReview,
    isLoading: isDownloadPdfPrequalificationReviewLoading,
  } = useMutation(downloadPdfPrequalification);
  const {
    mutateAsync: mutateAsyncDownloadPdfDynamicReview,
    isLoading: isDownloadPdfDynamicReviewLoading,
  } = useMutation(downloadPdfDynamicReview);
  const {
    mutate: mutateDownloadDocxPrequalificationReview,
    isLoading: isDownloadDocxPrequalificationReviewLoading,
  } = useMutation(downloadDocxPrequalification);
  const {
    mutateAsync: mutateAsyncDownloadDocxDynamicReview,
    isLoading: isDownloadDocxDynamicReviewLoading,
  } = useMutation(downloadDocxDynamicReview);
  const {
    mutate: mutateDownloadPdfSpecializeReview,
    isLoading: isDownloadPdfSpecializeReviewLoading,
  } = useMutation(downloadPdfSpecializeReview);
  const {
    mutate: mutateDownloadDocxSpecializeReview,
    isLoading: isDownloadDocxSpecializeReviewLoading,
  } = useMutation(downloadDocxSpecializeReview);

  const {
    mutate: mutateDownloadPdfFinancialReview,
    isLoading: isDownloadPdfFinancialReviewLoading,
  } = useMutation(downloadPdfFinancialReview);
  const {
    mutate: mutateDownloadDocxFinancialReview,
    isLoading: isDownloadDocxFinancialReviewLoading,
  } = useMutation(downloadDocxFinancialReview);

  const { mutate: mutateDownloadPdfMidtermReview, isLoading: isDownloadPdfMidtermReviewLoading } =
    useMutation(downloadPdfMidtermReview);
  const { mutate: mutateDownloadDocxMidtermReview, isLoading: isDownloadDocxMidtermReviewLoading } =
    useMutation(downloadDocxMidtermReview);

  const {
    mutate: mutateDownloadPdfAcceptanceReview,
    isLoading: isDownloadPdfAcceptanceReviewLoading,
  } = useMutation(downloadPdfAcceptanceReview);
  const {
    mutate: mutateDownloadDocxAcceptanceReview,
    isLoading: isDownloadDocxAcceptanceReviewLoading,
  } = useMutation(downloadDocxAcceptanceReview);

  const handleDownloadReview = async (res, value, type, fileType = 'pdf') => {
    if (res?.data) {
      const file = new Blob([res.data]);

      const academicLevelShorthand = getAcadamicLevelShortHand(
        value?.contact?.scientificProfile?.academicRank,
        value?.contact?.scientificProfile?.academicDegree,
        t,
        true
      );
      const filename = `Phieu nhan xet danh gia ${type} - ${academicLevelShorthand}${toNonAccentVietnamese(
        value?.contact?.fullname
      )}.${fileType}`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = filename;
      link.click();
    }
  };

  const handleDownloadPdf = async (value) => {
    setSelectedMemberId(value?.id);

    if (councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      const pdfData = {
        projectProposalId,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfPrequalificationReview(pdfData, {
          onSuccess: (res) => {
            handleDownloadReview(res, value, 'so tuyen');
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'so tuyen');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      const pdfData = {
        projectProposalId: prequalificationVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfSpecializeReview(pdfData, {
          onSuccess: (res) => {
            handleDownloadReview(res, value, 'chuyen mon');
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'chuyen mon');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      const pdfData = {
        projectProposalId: specializationVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfFinancialReview(pdfData, {
          onSuccess: (res) => {
            handleDownloadReview(res, value, 'tai chinh');
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'tai chinh');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      const pdfData = {
        projectProposalId: latestVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfMidtermReview(pdfData, {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);

              const academicLevelShorthand = getAcadamicLevelShortHand(
                value?.contact?.scientificProfile?.academicRank,
                value?.contact?.scientificProfile?.academicDegree,
                t,
                true
              );
              const filename = `Phieu nhan xet danh gia giua ki - ${academicLevelShorthand}${toNonAccentVietnamese(
                value?.contact?.fullname
              )}.pdf`;

              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'giua ki');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      const pdfData = {
        projectProposalId: latestVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfAcceptanceReview(pdfData, {
          onSuccess: (res) => {
            if (res?.data) {
              handleDownloadReview(res, value, 'nghiem thu');
            }
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'nghiem thu');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      const pdfData = {
        projectProposalId: latestVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'thanh ly');
      }
    }
  };

  const handleDownloadDocx = async (value) => {
    setSelectedMemberId(value?.id);

    if (councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxPrequalificationReview(
          {
            projectProposalId,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              handleDownloadReview(res, value, 'so tuyen', 'docx');
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'so tuyen', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxSpecializeReview(
          {
            projectProposalId: prequalificationVersion?.id,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              handleDownloadReview(res, value, 'chuyen mon', 'docx');
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'chuyen mon', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      if (!is1593(template, yearProposed)) {
        mutateDownloadDocxFinancialReview(
          {
            projectProposalId: prequalificationVersion?.id,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              handleDownloadReview(res, value, 'tai chinh', 'docx');
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template, yearProposed)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'tai chinh', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxMidtermReview(
          {
            projectProposalId: latestVersion?.id,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              if (res?.data) {
                const file = new Blob([res.data]);

                const academicLevelShorthand = getAcadamicLevelShortHand(
                  value?.contact?.scientificProfile?.academicRank,
                  value?.contact?.scientificProfile?.academicDegree,
                  t,
                  true
                );
                const filename = `Phieu nhan xet danh gia giua ki - ${academicLevelShorthand}${toNonAccentVietnamese(
                  value?.contact?.fullname
                )}.docx`;

                const link = document.createElement('a');
                link.href = URL.createObjectURL(file);
                link.download = filename;
                link.click();
              }
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'giua ky', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxAcceptanceReview(
          {
            projectProposalId: latestVersion?.id,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              if (res?.data) {
                handleDownloadReview(res, value, 'nghiem thu', 'docx');
              }
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'nghiem thu', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      const res = await mutateAsyncDownloadDocxDynamicReview({
        projectProposalId,
        councilId,
        councilMemberId: value?.id,
      });
      handleDownloadReview(res, value, 'thanh ly', 'docx');
    }
  };

  const sumScoreInputFormOfMember = (score) => {
    const sum =
      (score?.scoreImportance ?? 0) +
      (score?.scoreQuality ?? 0) +
      (score?.scoreProduct ?? 0) +
      (score?.scoreCapability ?? 0) +
      (score?.scoreSubCriteria ?? 0);

    return sum;
  };

  // #endregion Event

  const formatIndex = (value, { rowIndex }) => rowIndex + 1;

  const formatFullname = (value) =>
    `${getAcadamicLevelShortHand(
      value?.contact?.scientificProfile?.academicRank,
      value?.contact?.scientificProfile?.academicDegree,
      t,
      true
    )}${value?.contact?.fullname}`;

  const formatOrganization = (value) =>
    getOrganizationNameById(
      value?.contact?.organizationId,
      organizations,
      value?.contact?.specificOrganizationName,
      t
    );

  const formatRole = (value) => (
    <div className="flex">
      <div className="w-2 mr-2">
        {isShowValidScore &&
          isMemberAllowedReview(value) &&
          (value?.projectProposalCouncilMembers?.length === 0 ||
            value?.projectProposalCouncilMembers?.[0]?.isCalculateScore) && (
            <>
              <i
                className="tick-icon pi pi-check border-primary-700"
                data-pr-tooltip={t('council.member.fields.tooltipAllowedCalScore')}
                data-pr-position="left"
              />
              <Tooltip target=".tick-icon" />
            </>
          )}
      </div>
      {value?.councilMemberRole?.code && t(`council.role.${value?.councilMemberRole?.code}`)}
    </div>
  );

  const formatResult = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    if (resultStatus === 'notReviewed' && !isSubmitted) {
      return '';
    }

    let result = null;
    if (!is1593(template)) {
      if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
        const evaluateGeneral =
          value?.projectProposalCouncilMembers[0]?.midtermReview?.evaluateGeneral;
        if (evaluateGeneral != null) {
          result = evaluateGeneral === false ? 'Chưa đạt' : 'Đạt';
        } else {
          result = null;
        }
      }

      if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
        const scoreTarget = value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreTarget;
        const scoreContent =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreContent;
        const scoreApproach =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreApproach;
        const scoreProcess =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreProcess;
        const scoreApplicationProduct =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreApplicationProduct;
        const scorePublishedProduct =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scorePublishedProduct;
        const scoreTrainingProduct =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreTrainingProduct;
        const scoreReportQuality =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreReportQuality;

        const scoreTotal =
          scoreTarget +
          scoreContent +
          scoreApproach +
          scoreProcess +
          scoreApplicationProduct +
          scorePublishedProduct +
          scoreTrainingProduct +
          scoreReportQuality;

        result = scoreTotal || null;
      }
    } else if (is1593(template)) {
      if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
        const rank = value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.rank;
        if (rank && typeof rank === 'object') {
          Object.keys(rank).forEach((key) => {
            if (rank[key] === true) {
              result = t(`council.acceptanceReview.rank.${key}`);
            }
          });
        }
      }
    }

    return result;
  };

  const formatScore = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    if (resultStatus === 'notReviewed' && !isSubmitted) {
      return '';
    }

    if (isMemberAllowedReview(value)) {
      if (
        sumScoreInputFormOfMember(value?.projectProposalCouncilMembers?.[0]?.specializeReview) > 0
      ) {
        return sumScoreInputFormOfMember(
          value?.projectProposalCouncilMembers?.[0]?.specializeReview
        );
      }
      if (
        value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.evaluation?.totalScore > 0
      ) {
        return value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.evaluation
          ?.totalScore;
      }
    }

    return '';
  };

  const formatResultReviewPrequalification = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    // Check if the council is not reviewed and the member is not allowed to review
    if ((resultStatus === 'notReviewed' && !isSubmitted) || !isMemberAllowedReview(value)) {
      return null;
    }

    let resultReview;

    // Check with template review is not new (1593)
    if (yearProposed < 2025) {
      resultReview =
        value?.projectProposalCouncilMembers?.[0]?.prequalificationReview?.generalEvaluate;
    }
    // Check with template review is new (1593)
    else {
      const conclusion =
        value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.conclusion;
      if (conclusion?.qualified === true) {
        resultReview = true;
      } else if (conclusion?.notQualified === true) {
        resultReview = false;
      }
    }

    // If resultReview is null or undefined, return null
    if (resultReview === null || resultReview === undefined) {
      return null;
    }

    return (
      <Tag
        value={resultReview === true ? t('resultReview.qualified') : t('resultReview.notQualified')}
        severity={resultReview === true ? 'success' : 'danger'}
        style={{ minWidth: '6rem', textAlign: 'center' }}
        rounded
      />
    );
  };

  const formatStatus = (value) => {
    if (!isMemberAllowedReview(value)) {
      return null;
    }

    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    return (
      <Tag
        value={isSubmitted ? t('statusReview.submitted') : t('statusReview.notSubmitted')}
        severity={isSubmitted ? 'success' : 'info'}
        style={{ minWidth: '6rem', textAlign: 'center' }}
        rounded
      />
    );
  };

  const formatProposedExpenditure = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    if (resultStatus === 'notReviewed' && !isSubmitted) {
      return '';
    }

    const totalSuggestionExpenditureFromVNUHCM =
      value?.projectProposalCouncilMembers?.[0]?.financialReview
        ?.totalSuggestionExpenditureFromVNUHCM;
    const totalSuggestionExpenditureFromMobilizedCapital =
      value?.projectProposalCouncilMembers?.[0]?.financialReview
        ?.totalSuggestionExpenditureFromMobilizedCapital;

    if (!value?.projectProposalCouncilMembers?.[0]?.financialReview) {
      return '';
    }
    return (
      (totalSuggestionExpenditureFromVNUHCM ?? 0) +
      (totalSuggestionExpenditureFromMobilizedCapital ?? 0)
    )?.toLocaleString('vi');
  };

  const formatReview = (value) =>
    isMemberAllowedReview(value) ? (
      <div className="flex flex-wrap gap-1 justify-content-center">
        <Button
          icon="pi pi-history"
          tooltip={t('council.viewList.sendMailHistory')}
          tooltipOptions={{ position: 'left' }}
          onClick={() => {
            setSelectedMember(value);
            setIsViewMailLogs(true);
          }}
          disabled={!council?.completedAt}
        />
        <Button
          icon="pi pi-bell"
          severity="success"
          tooltip={`${t('council.viewList.sendOneMember')}
                          ${t('council.fields.numberSentMail')}: ${value?.mailSentCount ?? 0}
                          ${t('council.fields.lastSentMail')}: ${
            value.lastMailSentAt
              ? dayjs(value.lastMailSentAt).tz(TIMEZONE_VN).format('HH:mm:ss DD/MM/YYYY')
              : ''
          }`}
          tooltipOptions={{ position: 'left' }}
          onClick={() => {
            setSelectedMember(value);
            setIsVisibleConfirmSendRemind(true);
          }}
          disabled={!council?.completedAt}
        />
        {!isReadOnly &&
          (ability.can('create', 'specialize_review') ||
            ability.can('create', 'financial_review') ||
            ability.can('update', 'specialize_review') ||
            ability.can('update', 'financial_review') ||
            ability.can('create', 'prequalification_review') ||
            ability.can('update', 'prequalification_review')) && (
            <Link to={handleNavigateCreateOrUpdateReview(value)} state={{ fromInternal: true }}>
              <Button
                className="bg-orange-600 border-orange-600 hover:bg-orange-700"
                icon="pi pi-pencil"
                severity="warning"
                tooltip={t('council.review.createOrUpdate')}
                tooltipOptions={{ position: 'left' }}
              />
            </Link>
          )}
        {(ability.can('read', 'specialize_review') ||
          ability.can('read', 'financial_review') ||
          ability.can('read', 'prequalification_review')) && (
          <Button
            className="bg-red-600 border-red-600 hover:bg-red-700"
            icon={`pi ${
              (isDownloadPdfSpecializeReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadPdfFinancialReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadPdfPrequalificationReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadPdfMidtermReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadPdfAcceptanceReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadPdfDynamicReviewLoading && selectedMemberId === value?.id)
                ? 'pi-spin pi-spinner'
                : 'pi-file-pdf'
            }`}
            severity="info"
            tooltip={t('council.review.exportPdfFromForm')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => handleDownloadPdf(value)}
          />
        )}
        {/* eslint-disable indent */}
        {(ability.can('read', 'specialize_review') ||
          ability.can('read', 'financial_review') ||
          ability.can('read', 'prequalification_review')) && (
          <Button
            icon={`pi ${
              (isDownloadDocxSpecializeReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadDocxFinancialReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadDocxPrequalificationReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadDocxMidtermReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadDocxAcceptanceReviewLoading && selectedMemberId === value?.id) ||
              (isDownloadDocxDynamicReviewLoading && selectedMemberId === value?.id)
                ? 'pi-spin pi-spinner'
                : 'pi-file-word'
            }`}
            severity="info"
            tooltip={t('council.review.exportDocxFromForm')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => handleDownloadDocx(value)}
          />
        )}
        {(ability.can('read', 'specialize_review') ||
          ability.can('read', 'financial_review') ||
          ability.can('read', 'prequalification_review')) && (
          <Link to={handleNavigateReviewDetail(value)}>
            <Button
              className="bg-green-600 border-green-600 hover:bg-green-700"
              icon="pi pi-comment"
              severity="info"
              tooltip={t('council.review.detail')}
              tooltipOptions={{ position: 'left' }}
            />
          </Link>
        )}
      </div>
    ) : (
      <> </>
    );

  const formatAction = (value) => (
    <div className="flex flex-wrap gap-1 justify-content-center">
      <Button
        icon="pi pi-bell"
        severity="success"
        tooltip={`${t('council.viewList.sendOneMember')}
                          ${t('council.fields.numberSentMail')}: ${value?.mailSentCount ?? 0}
                          ${t('council.fields.lastSentMail')}: ${
          value.lastMailSentAt
            ? dayjs(value.lastMailSentAt).tz(TIMEZONE_VN).format('HH:mm:ss DD/MM/YYYY')
            : ''
        }`}
        tooltipOptions={{ position: 'left' }}
        onClick={() => {
          setSelectedMember(value);
          setIsVisibleConfirmSendRemind(true);
        }}
        disabled={!council?.completedAt}
      />
    </div>
  );

  const formatAttendance = (value) => {
    const isPresent = value?.isPresent;

    return (
      <div className="flex justify-content-center align-items-center">
        <Checkbox
          inputId={`attendance-${value.id}`}
          checked={isPresent !== false} // Default to true if null/undefined
          onChange={(e) => handleAttendanceChange(value.id, e.checked)}
          disabled={isReadOnly || updateMemberAttendanceMutation.isLoading}
          tooltip={
            isPresent !== false
              ? t('council.member.attendance.present')
              : t('council.member.attendance.absent')
          }
          tooltipOptions={{ position: 'top' }}
        />
        <label
          htmlFor={`attendance-${value.id}`}
          className={`ml-2 ${isPresent !== false ? 'text-green-600' : 'text-red-600'}`}
        >
          {isPresent !== false
            ? t('council.member.attendance.present')
            : t('council.member.attendance.absent')}
        </label>
      </div>
    );
  };

  return (
    members?.length > 0 && (
      <div className="grid">
        <div className="col-12">
          <DataTable
            value={members}
            className="p-datatable-gridlines"
            showGridlines
            dataKey="id"
            responsiveLayout="scroll"
          >
            <Column
              header={t('formLayout.numericalOrder')}
              body={formatIndex}
              style={{ width: '2rem', textAlign: 'center' }}
            />
            <Column
              header={`${t(
                'projectProposal.general.researchHumanResources.fields.academicRank'
              )}, ${t('projectProposal.general.researchHumanResources.fields.academicDegree')}, ${t(
                'projectProposal.general.researchHumanResources.fields.name'
              )}`}
              body={formatFullname}
              className="member-name"
            />
            <Column header="Email" field="contact.email" style={{ minWidth: '8rem' }} />
            <Column
              header={t('council.member.fields.role')}
              style={{ minWidth: '9rem' }}
              body={formatRole}
            />
            {/* Attendance column for all council types:
                - PRELIMINARY_SELECTION_COUNCIL (Sơ tuyển)
                - SPECIALIZATION_COUNCIL (Thẩm định chuyên môn)
                - FINANCE_COUNCIL (Thẩm định tài chính)
                - MIDTERM_DOCUMENT_COUNCIL (Hội đồng thẩm định hồ sơ giữa kỳ)
                - MIDTERM_REPORT_COUNCIL (Hội đồng báo cáo giữa kỳ)
                - ACCEPTANCE_COUNCIL (Nghiệm thu)
                - LIQUIDATION_COUNCIL (Thanh lý)
                - SPECIALIZATION_AND_FINANCE_COUNCIL (Gộp chuyên môn và tài chính)
            */}
            <Column
              header={t('council.member.attendance.title')}
              body={formatAttendance}
              style={{ minWidth: '10rem', textAlign: 'center' }}
            />
            <Column
              body={formatOrganization}
              header={t('projectProposal.general.researchHumanResources.fields.organization')}
              style={{ minWidth: '8rem' }}
            />
            {isShowScore && (
              <Column
                header={t('council.view.score')}
                body={formatScore}
                style={{ minWidth: '2rem', textAlign: 'center' }}
              />
            )}
            {isShowProposedExpenditure && (
              <Column
                header={t('council.view.proposedExpenditure')}
                body={formatProposedExpenditure}
                style={{ minWidth: '2rem', textAlign: 'center' }}
              />
            )}
            {isShowResult && (
              <Column
                body={formatResult}
                header={t('council.member.fields.result')}
                style={{ width: '4rem', maxWidth: '6rem' }}
              />
            )}

            {isShowResultReviewPrequalification && (
              <Column
                header={t('resultReview.title')}
                body={formatResultReviewPrequalification}
                style={{ minWidth: '8rem', textAlign: 'center' }}
              />
            )}

            {isShowStatus && (
              <Column
                header={t('council.fields.status')}
                body={formatStatus}
                style={{ minWidth: '8rem', textAlign: 'center' }}
              />
            )}

            <Column
              body={formatReview}
              header={t('council.view.review')}
              style={{ width: '12rem', maxWidth: '14rem' }}
              hidden={!isShowReview}
            />

            <Column
              body={formatAction}
              header={t('council.viewList.action')}
              style={{ width: '12rem', maxWidth: '14rem' }}
              hidden={!isHasActionColumn}
            />
          </DataTable>
        </div>

        <SendMailRemindAllMembersDialog
          refetch={refetch}
          visible={isVisibleConfirmSendRemind}
          setVisible={setIsVisibleConfirmSendRemind}
          council={{
            councilType: {
              code: COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL,
            },
            ...(council ?? {}),
          }}
          isOneMember
          recipient={selectedMember}
          hasProjects={hasProjects}
        />

        <ViewMailLogs
          visible={isViewMailLogs}
          setVisible={setIsViewMailLogs}
          params={{
            id: councilId,
            memberId: selectedMember?.id,
          }}
        />
      </div>
    )
  );
}

Members.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array.isRequired,
  council: PropTypes.shape().isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  councilType: PropTypes.shape().isRequired,
  setIsFormModalOpen: PropTypes.func,
  setIsDetailModalOpen: PropTypes.func,
  setCouncilMemberId: PropTypes.func,
  projectProposalId: PropTypes.string.isRequired,
  councilId: PropTypes.string.isRequired,
  isShowScore: PropTypes.bool,
  isShowProposedExpenditure: PropTypes.bool,
  isShowResult: PropTypes.bool,
  isShowReview: PropTypes.bool,
  reviewDeadline: PropTypes.string,
  isShowValidScore: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isHasActionColumn: PropTypes.bool,
  hasProjects: PropTypes.bool,
  yearProposed: PropTypes.number,
  template: PropTypes.number,
  refetch: PropTypes.func.isRequired,
  isShowStatus: PropTypes.bool,
  isShowResultReviewPrequalification: PropTypes.bool,
};

Members.defaultProps = {
  reviewDeadline: new Date().toISOString(),
  setIsFormModalOpen: () => null,
  setIsDetailModalOpen: () => null,
  setCouncilMemberId: () => null,
  isShowScore: false,
  isShowProposedExpenditure: false,
  isShowResult: false,
  isShowReview: true,
  isShowValidScore: false,
  isReadOnly: false,
  isHasActionColumn: false,
  hasProjects: false,
  yearProposed: null,
  template: null,
  isShowStatus: true,
  isShowResultReviewPrequalification: false,
};
